import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { WebService, lastValueFrom } from '@services';
import { childInventoryType } from '../../../purchase-orders/shared/purchase-order-state';

interface FamilyNode {
  InventoryID: number;
  ParentInventoryID: number | null;
  InventoryTypeName: string;
  QtyCreated: number;
  NetWeight: number;
  FinalPosition: string;
  WarehouseName: string;
  ConversionReason?: string;
  kind: string;
  children: FamilyNode[];
}

@Component({
  selector: 'app-inventory-family-tree',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './inventory-family-tree.component.html',
  styleUrl: './inventory-family-tree.component.scss'
})
export class InventoryFamilyTreeComponent implements OnChanges, OnDestroy {
  @Input() inventoryID!: number;
  @Input() compact = false;
  roots: FamilyNode[] = [];
  loading = false;
  error = '';
  count = 0;
  private requestVersion = 0;
  constructor(private web: WebService) {}
  ngOnChanges(): void { void this.load(); }
  ngOnDestroy(): void { this.requestVersion++; }
  async load(): Promise<void> {
    const version = ++this.requestVersion;
    this.roots = []; this.error = ''; this.count = 0;
    if (!this.inventoryID) return;
    this.loading = true;
    try {
      const rows = await lastValueFrom(this.web.getWebCall('inventory', 'coffee/inventory/lineage', { params: [{ name: 'InventoryID', value: this.inventoryID }], skipLoader: true }));
      if (version !== this.requestVersion) return;
      const nodes = new Map<number, FamilyNode>();
      for (const row of rows ?? []) {
        nodes.set(Number(row.InventoryID), { ...row, InventoryID: Number(row.InventoryID),
          ParentInventoryID: row.ParentInventoryID == null ? null : Number(row.ParentInventoryID),
          kind: row.IsFamilyRoot ? 'Original inventory' :
            row.SlackBag && Number(row.NetWeight) === 22 ? 'Royal Line Up' : childInventoryType(row), children: [] });
      }
      // A malformed relationship must never create a recursive template loop.
      for (const node of nodes.values()) {
        let parent = nodes.get(node.ParentInventoryID!);
        const visited = new Set<number>([node.InventoryID]);
        let ancestor = parent;
        while (ancestor && !visited.has(ancestor.InventoryID)) {
          visited.add(ancestor.InventoryID);
          ancestor = nodes.get(ancestor.ParentInventoryID!);
        }
        if (ancestor) parent = undefined;
        if (parent) parent.children.push(node); else this.roots.push(node);
      }
      this.count = nodes.size;
    } catch {
      if (version === this.requestVersion) this.error = 'The family tree could not be loaded. Please try again.';
    } finally { if (version === this.requestVersion) this.loading = false; }
  }
}
